import "./style.scss";

import * as React from "react";
import logoSrc from "../../../common/component/img/logo-vente-privee-group.png";

import { observer } from "mobx-react";

import { Form } from "../form/form";
import { Loader } from "../loader/loader";
import { LoginForm } from "./login-form";
import { context } from "../../../context/context";
import { Button } from "@mui/material";
import { User } from "../../../context/user";
import { QrCode } from "@mui/icons-material";
import { t } from "i18next";
import { UserSessionContextDispatcher } from "../../context/application-context/user-session-context-provider";
import { Dispatch } from "react";
import { IUserSessionContextDispatcherPayload } from "../../context/application-context/user-session-context-reducer";
import { GetUserProfileHandler } from "../../cqrs/query/get-user-profile/get-user-profile-handler";

interface ILoginProps {
    appName: string;
    login: (login: string, password: string) => Promise<{ access_token: string }>;
    enableSso?: boolean;
    onSSORequested?: () => Promise<void>;
    enableQrLogin?: boolean;
    onQrRequested?: () => void;
    onForgotPasswordRequested?: (userName?: string) => void;
    onSuccess: () => void;
}

@observer
export class Login extends Form<LoginForm, ILoginProps> {
    public static contextType = UserSessionContextDispatcher;
    public declare context: Dispatch<IUserSessionContextDispatcherPayload>;

    constructor(props: ILoginProps) {
        super(props, {
            title: props.appName,
            isDefaultEdit: true,
            isCancelEnabled: false,
            isActionInFooter: true,
            renderFooter: () => {
                return <div className="loginFooter">
                    <img className="logo" src={logoSrc} />
                </div>;
            },
        });
    }

    protected getDataKey() {
        return undefined;
    }

    protected async load(_dataKey: any) {
        return new LoginForm();
    }

    protected renderForm(data: LoginForm) {
        return <div className="login">
            {this.getField(data, "login", { isRequired: true })}
            {this.getField(data, "password", { isRequired: true })}
            <div className="spinner">
                <Loader isBackground={true} />
            </div>
        </div>;
    }

    protected renderActions() {
        return <div className="buttonContainer">
            <Button color={"secondary"} variant={"contained"} type={"submit"} autoFocus key="submit-btn">
                {t("login.action.submit")}
            </Button>
            {context.isSsoActive() && this.renderSsoButton()}
            {!context.isSsoActive() && !this.props.enableQrLogin && this.renderForgotPasswordButton()}
            {this.props.enableQrLogin && this.props.onQrRequested && this.renderQrCodeButton()}
        </div>;
    }

    protected renderSsoButton() {
        if (!this.props.onSSORequested) {
            return;
        }

        return <Button variant="contained" onClick={async () => await this.props.onSSORequested!()} key="sso-btn">Login with SSO</Button>;
    }

    private renderQrCodeButton() {
        return <Button variant="contained" onClick={() => this.props.onQrRequested!()} key="qr-btn" endIcon={<QrCode />}>
            {t("login.label.qrLogin")}
        </Button>;
    }

    private renderForgotPasswordButton() {
        if (!this.props.onForgotPasswordRequested) {
            return <></>;
        }

        return <Button variant="contained" color={"inherit"} onClick={() => this.props.onForgotPasswordRequested!(this.formModel.data.login ?? "")} key="password-reminder">
            {t("login.action.passwordReset")}
        </Button>;
    }

    protected async submitForm(data: LoginForm) {
        const response = await this.props.login(data.login, data.password);

        if (response !== null) {
            const user = new User(data.login, response.access_token, "ds");
            context.setCurrentUser(user);
            const profile = await GetUserProfileHandler();
            await context.initAuthenticatedContextAsync(true, profile);
            this.context({ action: "authenticate-user", user, profile });
        }
    }

    protected onSuccess(_reponse: any) {
        this.props.onSuccess();
    }
}
